// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import moment from 'moment';
import HighchartOptionsBuilder from 'share/components/charting/highcharts/Izenda.HighchartOptionsBuilder';
// for null value >> treat it as zero
const DEFAULT_NUMERIC_VALUE = 0;
const TICK_SPACE = 30;

import { exportClass } from 'share/Izenda.ServiceProvider';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import ReportPartStyleFactories from 'share/factories/Izenda.ReportPartStyleFactories';
import { UNDEFINED_VALUE } from 'logic/data/Izenda.UIConstants';
import constants from 'logic/data/Izenda.ReportPartChartConstants';
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import {
  DATA_TYPES,
  REPORT_PART_TYPE
} from 'logic/data/Izenda.ReportDesignerConstants';
import { DATE_TIME_FORMAT as DATE_TIME_FORMATS } from 'logic/data/Izenda.TimeFormatConstants';
import {
  TIME_FORMAT as DEFAULT_TIME_FORMAT
} from 'logic/data/Izenda.AppGlobalConstants';
import { SORT_ORDERS } from 'logic/data/Izenda.ReportPartFieldConstants';
import AppGlobal from 'Izenda.AppGlobal';

const isChartType = ReportPartStyleFactories.isReportPartStyle(
  REPORT_PART_TYPE.Chart
);
const getAxisType = HighchartUtils.getAxisType;
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
const getName = ChartDataParser.getName;
import {
  some,
  get,
  getOr,
  uniq,
  nth,
  findIndex,
  merge,
  isEmpty,
  reverse,
  isNil,
  flow,
  map,
  first,
  each,
  concat,
  head,
  has,
  size,
  find,
  findKey,
  isObject,
  filter,
  isDate,
  sortBy,
  last,
  forEach,
  assignIn,
  keys,
  upperFirst,
  toString
} from 'lodash/fp';

@exportClass('DefaultChartOptionsBuilder')
export default class DefaultChartOptionsBuilder extends HighchartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} userOptions Highchart options
   * @param {Object} dataParser Chart data
   * @returns {Object}
   */
  buildOptionsByType(
    visualType: string,
    userOptions: any,
    dataParser: any
  ): any {
    const { LABELS, VALUES, SEPARATORS } = constants.ContainerName;
    const xAxisContainerName = getOr(
      LABELS,
      'X_AXIS_CONTAINER_NAME',
      userOptions
    );
    const yAxisContainerName = getOr(
      VALUES,
      'Y_AXIS_CONTAINER_NAME',
      userOptions
    );
    const separatorContainerName = getOr(
      SEPARATORS,
      'SEPARATOR_CONTAINER_NAME',
      userOptions
    );

    const dataStructure = dataParser.dataStructure || {};
    const yAxisContainerFields = dataStructure[yAxisContainerName];
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};

    const xAxisField = nth(
      dataParser.expandedLevel,
      dataStructure[xAxisContainerName]
    );

    function axisTickPositions(axisField, axisOptions, interval) {
      // Only apply tick Position for Date Time format and linear type
      const fieldDataType = get('reportPartElm.fieldDataType', axisField);
      const { DATE_TIME, TIME } = DATA_TYPES;
      if (
        isEmpty(xAxisType.categories)
        && (fieldDataType === DATE_TIME|| fieldDataType === TIME)
        && interval
        && this.dataMax !== null
        && this.dataMin !== null
      ) {
        const positions = [];
        const fieldFormatData = xAxisField.reportPartElm.fieldFormatData;
        let cloneInterval = interval;
        const intervalKey = getOr(
          's',
          'INTERVAL_VALUE',
          DATE_TIME_FORMATS[fieldFormatData]
        );
        const tickValue = moment(axisOptions.min || this.dataMin);
        const maxValue = moment(this.dataMax);

        // Calculate Tick Number
        // If actual tick number > maxTickNumber use maxTickNumber and
        const containerWidth = $izenda(chartContainer).width();
        const maxTickNumber = containerWidth / TICK_SPACE;
        const momentTickValue = moment(new Date(0)).add(
          cloneInterval,
          intervalKey
        );
        const eachTickValue = Date.parse(momentTickValue);
        const actualTicks =
          (Date.parse(maxValue) - Date.parse(tickValue)) / eachTickValue;

        if (actualTicks > maxTickNumber) {
          cloneInterval = (
            (actualTicks * cloneInterval) /
            maxTickNumber
          ).toFixed(0);
        }

        while (tickValue <= maxValue) {
          positions.push(tickValue.valueOf());

          tickValue.add(cloneInterval, intervalKey);
        }

        return positions;
      }

      return null;
    }

    /**
     * Get YAxis Option by metrics
     */
    const getYAxisOptionByMetrics = series => {
      let option;
      const commonXYAxis = get('commonXYAxis', userOptions);
      const length = getNumberFieldInMetricsContainer(series);
      if (length > 1) {
        option = get('metricAllOption', commonXYAxis);
      } else {
        const yAxisField = get('yAxisField', nth(0, series));
        option = this.getAxisFieldValue(
          yAxisField,
          'axisOption',
          izendaSeriesConfig
        );
      }
      return merge(option, get('yAxis', commonXYAxis));
    };

    /**
     * Get static threshold by metrics
     */
    const getYStaticThresholdOptionByMetrics = series => {
      const length = getNumberFieldInMetricsContainer(series);

      if (length > 1) {
        // if length > 1 ==> user selected All metric
        const staticThresholdLineMetric = [];
        each(serie => {
          const yAxisField = get('yAxisField', serie);
          const thresholds = this.getAxisFieldValue(
            yAxisField,
            'threshold',
            izendaSeriesConfig
          );
          if (size(thresholds) > 0) {
            staticThresholdLineMetric.push(...thresholds);
          }
        }, series);
        return staticThresholdLineMetric;
      } else {
        // user selected a metric
        const yAxisField = get('yAxisField', nth(0, series));
        const thresholds = this.getAxisFieldValue(
          yAxisField,
          'threshold',
          izendaSeriesConfig
        );
        return thresholds;
      }
    };

    /**
     * Get number of metrics
     */
    const getNumberFieldInMetricsContainer = series => {
      return flow(
        map(get('yAxisField.fieldNameAlias')),
        uniq,
        size
      )(series);
    };

    /**
     * Get static threshold option
     */
    const getXThresholdOption = () => {
      return this.getAxisFieldValue(
        xAxisField,
        'threshold',
        izendaSeriesConfig
      );
    };

    const useUniqCategories = () => {
      const separatorContainer = dataStructure[separatorContainerName];
      const hasSeparator = separatorContainer && separatorContainer.length > 0;
      if(hasSeparator) return true;

      const daysOldValuesSeries = find(series => {
        const isDaysOldSeries = get('fieldElement.properties.dataFormattings.functionInfo.name', series) === 'Days Old';
        if(!isDaysOldSeries) return false;

        const daysOldFieldName = get('fieldElement.name', series);
        const daysOldFieldContainerName = findKey(container => some(field => field.fieldNameAlias === daysOldFieldName, container), dataStructure);

        // if daysOldFieldContainerName == null then this means that a metric other than DaysOld has been selected
        return isNil(daysOldFieldContainerName) || daysOldFieldContainerName === 'values';
      }, izendaSeriesConfig);

      return isNil(daysOldValuesSeries);
    };

    const getValueFromSeriesConfig = this.getValueFromSeriesConfig;
    const xAxisOptions = this.getXAxisOption(xAxisField, userOptions);
    const xAxisFieldConfig = getValueFromSeriesConfig(
      xAxisField,
      izendaSeriesConfig
    );
    
    const xAxisInfo = dataParser.getDataByField(xAxisField, null, xAxisFieldConfig);
    const xAxisName = xAxisInfo.name;
    const xAxisType = getAxisType(xAxisField, xAxisInfo.data, xAxisOptions, useUniqCategories());
    const xAxisData = xAxisType.categories;
    const xAxisOption = this.getAxisFieldValue(
      xAxisField,
      'axisOption',
      izendaSeriesConfig
    );
    const tickInterval = get('izCategory', xAxisOption)
      ? getOr(null, 'tickCategoryInterval', xAxisOption)
      : get('tickInterval', xAxisOption);
    const chartContainer = userOptions.commonActions.chartContainer;

    // important - use defaul-value (zero) for all point which dont have data
    let series =
      !isNil(xAxisField) && !isNil(yAxisContainerFields)
        ? this.getSeries(
            dataParser,
            userOptions,
            xAxisField,
            yAxisContainerFields
          )
        : [];

    const yAxisOptions = series && getYAxisOptionByMetrics(series);
    const separatorField = nth(
      dataParser.expandedLevel,
      dataStructure[separatorContainerName]
    );
    const nullIndex = findIndex(ele => ele === null, xAxisData);

    nullIndex > -1 && xAxisData.splice(nullIndex, 1, 'null');

    const sortLinear = get('sort.valueXAxisForLinear', userOptions);

    const needReverse =
      sortLinear === SORT_ORDERS.DESC.value
        ? userOptions.chart.type !== 'bar'
        : undefined;
    const needReverseSeries =
      sortLinear === SORT_ORDERS.DESC.value &&
      (userOptions.chart.type === 'column' || userOptions.chart.type === 'bar');
    needReverseSeries && (series = reverse(series));

    const chartType = get('chart.type', userOptions);
    this.configureRegression(izendaSeriesConfig, series);

    const chartOptions = {
      series,
      xAxis: {
        axisName: xAxisName,
        categories: xAxisData,
        type: xAxisType.type,
        labels: {
          formatter: HighchartUtils.getFormattedLabelAxis,
          useHTML: true
        },
        plotLines: getXThresholdOption(),
        tickPositioner() {
          return axisTickPositions.bind(this)(
            xAxisField,
            xAxisOptions,
            tickInterval
          );
        },
        tickInterval,
        // Use this "reversed" option when xAxis type = Linear (field type is numeric/ money/ datetime/ time)
        // When chart type = Bar, it's actually a Column type + Inverted
        reversed: needReverse
      },

      yAxis: {
        gridLineColor: 'transparent',
        labels: {
          formatter: HighchartUtils.getFormattedLabelAxis,
          useHTML: true
        },
        plotLines: getYStaticThresholdOptionByMetrics(series)
      },
      legend: {
        labelFormatter() {
          if (isChartType(
            upperFirst(chartType),
            CHART_TYPE.Pie,
            CHART_TYPE.Funnel,
          )) {
            const separatorConfig = getValueFromSeriesConfig(
              separatorField,
              izendaSeriesConfig
            );
            const xAxisFieldConfig = getValueFromSeriesConfig(
              xAxisField,
              izendaSeriesConfig
            );

            const { fieldElement, fieldDataType } = xAxisFieldConfig;
            const record = get('record', this);
            const columnName = isNil(separatorConfig)
              ? get('columnName', xAxisField)
              : get('series.options.nameField.columnName', this);

            const fieldValues = ReportPartUtils.getFieldValue(columnName, record);
            const valueStringFormated = HighchartUtils.getFormatedValueByStringFormat(
              fieldElement, fieldValues.originalValue, fieldDataType
            );

            if (!isNil(valueStringFormated)) {
              fieldValues.value = valueStringFormated;
            }
            return dataParser.getFormatData(fieldValues, xAxisFieldConfig, {
              element: get('series.userOptions.nameField', this),
              record: record,
              fieldValues
            });
          }
          return this.name;
        }
      }
    };

    chartOptions.yAxis = merge(chartOptions.yAxis, yAxisOptions || {});
    chartOptions.xAxis = merge(chartOptions.xAxis, xAxisOptions || {});

    const removedAxisOption = {
      minorTickLength: 0,
      tickLength: 0,
      title: {
        text: null
      },
      labels: {
        enabled: false
      }
    };

    if (!get('izendaHiddenAllAxis', userOptions.izendaOptions)) {
      HighchartUtils.setDefaultAxisName(chartOptions, dataParser);
    } else {
      chartOptions.yAxis = merge(chartOptions.yAxis, removedAxisOption);
      chartOptions.xAxis = merge(chartOptions.xAxis, removedAxisOption);
    }

    // add sub-report to x-axis
    !(userOptions.izMultiLevel && isChartType(this.chartOptions.chartType, CHART_TYPE.TreeMap))
      && HighchartUtils.addPointActionLink(
      true,
      userOptions,
      chartOptions.xAxis.labels,
      xAxisName,
      get(xAxisName, izendaSeriesConfig)
    );

    // Disable colorByPoint
    if (userOptions.izDisableMultiColor) {
      const plotOptions = {
        series: {
          colorByPoint: false
        }
      };

      chartOptions.plotOptions = merge(chartOptions.plotOptions, plotOptions);
    }

    return chartOptions;
  }

  /**
   * Get value from izendaSeriesConfig
   * Because DB column name may have dot character so we have to enlosed by '[]'
   * @field {*} axisField: X or X axisField
   * @izendaSeriesConfig:  all configuration of series
   */
  getValueFromSeriesConfig(field, izendaSeriesConfig) {
    const fieldName = getName(field);
    return get(`['${fieldName}']`, izendaSeriesConfig);
  }

  /**
   * Get X Axis options
   * @param {*} axisField: X or X axisField
   * @param {*} userOptions: user option build from property panel
   */
  getXAxisOption(xAxisField, userOptions) {
    const commonXYAxis = get('commonXYAxis.xAxis', userOptions);
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    const options = this.getAxisFieldValue(
      xAxisField,
      'axisOption',
      izendaSeriesConfig
    );
    const xAxisOption = merge(options, commonXYAxis);

    return HighchartUtils.setTickIntervalForXAxis(
      xAxisOption,
      get('reportPartElm.fieldDataType', xAxisField)
    );
  }

  /**
   * Get X or Y Axis Field Value
   * Because DB column name may have dot character so we have to enlosed by '[]'
   * @param {*} axisField: X or X axisField
   * @param {*} propName: property name of axisField
   * @izendaSeriesConfig:  all configuration of series
   */
  getAxisFieldValue(axisField, propName, izendaSeriesConfig) {
    const izendaSeriesConfigValue = this.getValueFromSeriesConfig(
      axisField,
      izendaSeriesConfig
    );
    return get(`['${propName}']`, izendaSeriesConfigValue);
  }

  /**
   * Build series
   * @param {*} dataParser: The data was formatted for highchart
   * @param {*} userOptions: The options user are able to select
   * @param {*} xAxisField: The field of X container
   * @param {*} yAxisFields: The field of Y container
   */
  getSeries(dataParser, userOptions, xAxisField, yAxisFields) {
    let series = [];
    const { SEPARATORS } = constants.ContainerName;
    const separatorContainerName = getOr(
      SEPARATORS,
      'SEPARATOR_CONTAINER_NAME',
      userOptions
    );
    const dataStructure = dataParser.dataStructure || {};
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    const xAxisInfo = dataParser.getDataByField(xAxisField);
    const xAxisOptions = this.getXAxisOption(xAxisField, userOptions);
    const xAxisType = getAxisType(xAxisField, xAxisInfo.data, xAxisOptions);
    const xAxisName = xAxisInfo.name;
    const xAxisConfig = izendaSeriesConfig[xAxisName];
    // 1. parse data
    if (dataParser.hasSeparator(separatorContainerName)) {
      const separatorField = first(dataStructure[separatorContainerName]);

      const groupNames = [];
      const groupData = dataParser.groupData(separatorField, groupNames);

      // each data for each group
      each(groupKey => {
        const separatorSeriesOption = this.getValueFromSeriesConfig(
          separatorField,
          izendaSeriesConfig
        );

        const record = head(groupData[groupKey]);
        const { originalValue } = ReportPartUtils.getFieldValue(separatorField.columnName, record);
        const {reportPartElm, fieldDataType} = separatorField;
        const formatedValue = HighchartUtils.getFormatedValueByStringFormat(reportPartElm, groupKey, fieldDataType);

        const fieldValue = {
          value: isNil(formatedValue) ? groupKey : formatedValue,
          originalValue: originalValue
        };

        series = concat(
          series,
          getSeriesData(
            xAxisField,
            groupData[groupKey],
            {
              field: separatorField,
              rawValue: groupKey === UNDEFINED_VALUE ? '' : groupKey,
              value: dataParser.getFormatData(fieldValue, separatorSeriesOption, {
                element: {
                  columnName: separatorField.columnName,
                  reportPartElm: separatorSeriesOption.fieldElement
                },
                record: record
              }),
              groupNamesSize: groupNames.length
            },
            !isNil(xAxisType.categories) ? uniq(xAxisType.categories) : null,
            yAxisFields
          )
        );
      }, groupNames);
    } else {
      series = concat(
        series,
        getSeriesData(xAxisField, dataParser.records, null, null, yAxisFields)
      );
    }

    // 2. set config to series
    addConfig(series);

    // 3. return
    return series;

    function addConfig(series) {
      each(parseSerieData, series);

      function parseSerieData(serie) {
        const serieData = serie.data;
        let tempData;

        // For Category Type : remove x value
        if (xAxisType.categories) {
          tempData = removeXValue(serieData);
        } else {
          switch (xAxisType.type) {
            case 'linear':
              parseData(serieData);
              tempData = sortData(serieData);
              break;

            case 'datetime':
              parseData(serieData, point => {
                const fieldDataType = get('fieldDataType', xAxisConfig);
                const pattern = fieldDataType === DATA_TYPES.TIME
                  ? getOr(DEFAULT_TIME_FORMAT,'fieldFormatData', xAxisConfig)
                  : get('fieldFormatData', xAxisConfig);

                point.xRawData = point.x;
                point.x = convertToDateTimeFormat(
                  pattern,
                  point.x,
                  false
                );
              });

              tempData = sortData(serieData);
              break;
          }
        }
        // update data
        serie.data = tempData;

        function sortData(serieData) {
          let x;
          return flow(
            filter(item => {
              x = item.x;
              return isDate(x) ? !!x.getTime() : !!x;
            }),
            sortBy('x')
          )(serieData);
        }

        function parseData(serieData, updateData) {
          return forEach(updateData, serieData);
        }

        function removeXValue(serieData) {
          parseData(serieData, point => {
            point.xRawData = point.x;
            delete point.x;
          });
          return serieData;
        }
      }
    }

    function getSeriesData(
      xAxisField,
      source,
      groupInfo,
      basedXAxisData,
      yAxisFields
    ) {
      const seriesObj = {};
      initData(seriesObj, source, yAxisFields);

      // loop records
      each(record => {
        updateData(seriesObj, ReportPartUtils.getOriginalValue(xAxisField, record), record, yAxisFields, xAxisField);
      }, source);

      return map(serie => {
        basedXAxisData &&
          buildData(basedXAxisData, serie, DEFAULT_NUMERIC_VALUE);

        return serie;
      }, seriesObj);

      function buildData(basedXAxisData, serie, defaultVal) {
        const basedValues = [];
        const values = serie.data;
        const labels = map(get('x'), serie.data);
        let foundIndex;
        let item;

        each(xAxisData => {
          foundIndex = labels.indexOf(xAxisData);

          if (foundIndex > -1) {
            item = values[foundIndex];
          } else {
            item = {
              x: xAxisData,
              y: defaultVal,
              isDefaultValue: true
            };
          }

          basedValues.push(item);
        }, basedXAxisData);

        serie.data = basedValues;
      }

      function getSeriePartNames(field) {
        const fieldNamesParts = ChartDataParser.getPartNames(field);
        const fieldName = getName(field);

        return {
          name: last(fieldNamesParts),
          data: size(fieldNamesParts) > 1 ? head(fieldNamesParts) : null
        };
      }

      function updateData(source, xaxisData, record, yAxisFields, xAxisField) {
        const yAxisData = {};
        let yVal;
        let point;

        // loop by value-fields
        each(field => {
          getYValue(field);
        }, yAxisFields);

        for (const yAxisName in yAxisData) {
          const yRawData = yAxisData[yAxisName];
          const isValidValue = !isNil(yRawData) && yRawData !== '';
          yVal = isValidValue ? yRawData : DEFAULT_NUMERIC_VALUE;

          point = {
            x: xaxisData,
            record
          };

          if (!isValidValue) {
            point.yRawData = yRawData;
          }

          if (isObject(yVal)) {
            point = assignIn(point, yVal);
          } else {
            point.y = yVal;
          }

          source[yAxisName].data.push(point);
        }

        function getYValue(field, hasData) {
          const partNames = getSeriePartNames(field);

          const value = ReportPartUtils.getOriginalValue(field, record);
          let partValue;

          if (isNil(partNames.data)) {
            yAxisData[partNames.name] = value;
          } else {
            partValue = {};
            partValue[partNames.data] = value;

            if (has(partNames.name, yAxisData)) {
              yAxisData[partNames.name] = assignIn(yAxisData[partNames.name], partValue);
            } else {
              yAxisData[partNames.name] = partValue;
            }
          }
        }
      }

      function initData(source, records, yAxisFields) {
        let partNames;
        const groupName = get('rawValue', groupInfo);
        const numMetric = size(yAxisFields);

        each(field => {
          partNames = getSeriePartNames(field);

          source[partNames.name] = assignIn(
            {
              separatorField: get('field', groupInfo),
              separatorValue: groupName,
              name: toString(getSerieName(groupInfo || {}, partNames.name)),
              xAxisField,
              yAxisField: field,
              data: [],
              dataLabels: {
                formatter: function getDataLabelFormat() {
                  if (!isNil(this.y)) {

                    const isDefaultValue = get(
                      'options.isDefaultValue',
                      this.point
                    );
                    const valueLabel = get(
                      'data',
                      HighchartUtils.getFormattedDataByPoint(
                        'y',
                        this.point,
                        true
                      )
                    );
                    return !isDefaultValue
                      ? HighchartUtils.showText(valueLabel)
                      : null;
                  }

                  if (!isNil(this.value)) {
                    return get(
                      'data',
                      HighchartUtils.getFormattedDataByPoint(
                        'value',
                        this.point,
                        true
                      )
                    );
                  }

                  return '';
                }
              }
            },
            get('chartOptions', izendaSeriesConfig[partNames.name])
          );
        }, yAxisFields);

        function getSerieName(groupInfo, yAxisFieldName) {
          const groupName = isNil(groupInfo.rawValue) && isNil(groupInfo.value)
            ? String.commonStringValue(
                groupInfo.rawValue,
                AppGlobal.advancedSettings.other.convertNullToEmptyStringValue
              )
            : get('value', groupInfo);
          const isShowGroupName = groupInfo.groupNamesSize >= 1;
          const isGuid = String.isGuidFormat(yAxisFieldName);

          if (numMetric > 1 && !isGuid) {
            return (
              (!isNil(groupName) && isShowGroupName ? groupName + ' - ' : '') +
              yAxisFieldName
            );
          } else {
            return isShowGroupName ? groupName : yAxisFieldName;
          }
        }
      }
    }
  }

  /**
   * Configure regression line for series
   * @param {Array<Object>} izendaSeriesConfig
   * @param {Array<Object>} series
   */
  configureRegression(izendaSeriesConfig: Array<any>, series: Array<any>) {
    if (isEmpty(izendaSeriesConfig) || isEmpty(series)) return;

    forEach(serie => {
      let serieConfig = izendaSeriesConfig[serie.name];
      if (!serieConfig && !isNil(serie.nameField)) {
        const fieldNameAlias = serie.nameField.fieldNameAlias;
        const serieConfigKey = find(key => key === fieldNameAlias, keys(izendaSeriesConfig));
        serieConfig = izendaSeriesConfig[serieConfigKey];
      }
      const regression = get('regression', serieConfig);
      const regressionBySerie = {
        regression: !!regression,
        regressionSettings: regression
      };

      Object.assign(serie, regressionBySerie);
    }, series);
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.DefaultChartOptionsBuilder.js